import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBlockedUsers } from '@/hooks/useBlockedUsers';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, ShieldOff, ShieldCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ProfileLite {
  id: string;
  name: string;
  profile_image: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BlockedUsersDialog({ open, onOpenChange }: Props) {
  const { blockedIds, unblock, loading } = useBlockedUsers();
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<ProfileLite[]>([]);
  const [fetchingProfiles, setFetchingProfiles] = useState(false);
  const [unblockingId, setUnblockingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const ids = Array.from(blockedIds);
    if (ids.length === 0) {
      setProfiles([]);
      return;
    }
    setFetchingProfiles(true);
    (async () => {
      try {
        // Use the SECURITY DEFINER function to fetch each profile (bypasses strict RLS)
        const fetched: ProfileLite[] = [];
        for (const id of ids) {
          const { data } = await supabase.rpc('get_chat_participant_profile', {
            _profile_id: id,
          });
          if (data && data[0]) {
            fetched.push({
              id: data[0].id,
              name: data[0].name,
              profile_image: data[0].profile_image,
            });
          } else {
            fetched.push({ id, name: 'Unknown user', profile_image: null });
          }
        }
        setProfiles(fetched);
      } finally {
        setFetchingProfiles(false);
      }
    })();
  }, [open, blockedIds]);

  const handleUnblock = async (id: string, name: string) => {
    setUnblockingId(id);
    const ok = await unblock(id);
    setUnblockingId(null);
    if (ok) {
      toast({ title: 'Unblocked', description: `${name} can now message you again.` });
      setProfiles((prev) => prev.filter((p) => p.id !== id));
    } else {
      toast({ title: 'Failed to unblock', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="h-5 w-5" /> Blocked users
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1">
          <div className="p-4">
            {(loading || fetchingProfiles) && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {!loading && !fetchingProfiles && profiles.length === 0 && (
              <div className="text-center py-10 text-sm text-muted-foreground">
                You haven't blocked anyone.
              </div>
            )}

            <div className="space-y-2">
              {profiles.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 p-2 rounded-lg border"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={p.profile_image || undefined} />
                    <AvatarFallback>{p.name?.[0]?.toUpperCase() || '?'}</AvatarFallback>
                  </Avatar>
                  <p className="flex-1 font-medium truncate">{p.name}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUnblock(p.id, p.name)}
                    disabled={unblockingId === p.id}
                  >
                    {unblockingId === p.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <ShieldCheck className="h-3 w-3 mr-1" /> Unblock
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
